import type {AccountBalance,CodexNotification} from '@reados/contracts';
import {ApiError} from './api';

export const INSUFFICIENT_BALANCE_MESSAGE='余额不足，请充值或兑换后重试。';
export const BALANCE_RECOVERY_INTERVAL_MS=3000;
export const BALANCE_RECOVERY_TIMEOUT_MS=6000;
export function isInsufficientBalance(error:unknown):boolean {
 return error instanceof ApiError&&error.status===402&&error.code==='INSUFFICIENT_BALANCE';
}
function record(value:unknown):Record<string,unknown>{return value&&typeof value==='object'&&!Array.isArray(value)?value as Record<string,unknown>:{};}
/** Only explicit machine codes identify a wallet failure. Human text and HTTP
 * status alone never turn an unrelated sandbox/provider failure into one. */
export function isInsufficientBalanceEvent(event:CodexNotification):boolean {
 if(!['error','reados/error','turn/failed','turn/completed'].includes(event.method))return false;
 const params=event.params,turn=record(params.turn);
 return [params,record(params.error),record(turn.error)].some(value=>value.code==='INSUFFICIENT_BALANCE');
}
export function hasPositiveBalance(balance:AccountBalance):boolean {
 return balance.currency==='CNY'&&balance.balanceStatus==='active'&&/^[0-9]+$/.test(balance.balanceNanoCny)&&BigInt(balance.balanceNanoCny)>0n;
}
export interface BalanceRecoveryEnvironment {
 read:(signal:AbortSignal)=>Promise<AccountBalance>;
 every:(callback:()=>void,milliseconds:number)=>()=>void;
 onFocus:(callback:()=>void)=>()=>void;
 onVisible:(callback:()=>void)=>()=>void;
 after?:(callback:()=>void,milliseconds:number)=>()=>void;
}
/** A blocked conversation owns this watcher. Failure leaves its real admission
 * error visible; only an authoritative positive wallet may clear it. */
export function watchBalanceRecovery(environment:BalanceRecoveryEnvironment,onRecovered:()=>void):()=>void {
 let stopped=false,request:AbortController|null=null,cancelTimeout:(()=>void)|null=null;
 const check=async()=>{
  if(stopped||request)return;
  const controller=new AbortController();request=controller;
  let cancel=()=>{};
  try{
   const timeout=new Promise<null>(resolve=>{const after=environment.after??((callback,milliseconds)=>{const timer=setTimeout(callback,milliseconds);return()=>clearTimeout(timer);});cancel=after(()=>{controller.abort();resolve(null);},BALANCE_RECOVERY_TIMEOUT_MS);cancelTimeout=cancel;});
   const balance=await Promise.race([environment.read(controller.signal),timeout]);
   if(balance&&!stopped&&!controller.signal.aborted&&hasPositiveBalance(balance)){stop();onRecovered();}
  }
  catch{/* A failed read is not evidence that the balance recovered. */}
  finally{cancel();if(request===controller){request=null;cancelTimeout=null;}}
 };
 const cleanup=[environment.every(()=>void check(),BALANCE_RECOVERY_INTERVAL_MS),environment.onFocus(()=>void check()),environment.onVisible(()=>void check())];
 function stop(){if(stopped)return;stopped=true;request?.abort();request=null;cancelTimeout?.();cancelTimeout=null;for(const remove of cleanup)remove();}
 void check();return stop;
}
